//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Grid from '@mui/material/Unstable_Grid2' // Grid version 2
import { EthAddr } from '@shared/SchemaTypes'
import { PoolBaseOverviewCTOSchema } from '@shared/sharedTypes'
import React from 'react'
import { useParams } from 'react-router-dom'

import ViewField from '@react/components/elements/ViewField'
import { OtStoreState, useOtSelector } from '@react/redux/store'

import { SectionHeader } from '@react/components/common/SectionHeader'

import Box from '@mui/material/Box'
import PoolDetailsSkeleton from '@react/components/skeletons/PoolDetailsSkeleton'
import { logOT } from '@shared/loggingShared'
import { PoolLytOverviewCTO, PoolLytOverviewCTOSchema } from '@shared/PoolLytOverviewCTO'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

export default function LytPoolOverview(props: { poolAddr?: EthAddr }): React.ReactElement {
  const p = useParams().poolAddr
  const poolAddr = (props.poolAddr ? props.poolAddr : p) as EthAddr

  const poolLytOverview = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolLytOverviewCTO
  )

  const navigate = useNavigate()

  useEffect(() => {
    if (!poolLytOverview) {
      const timeout = setTimeout(() => {
        if (!poolLytOverview) {
          logOT.info('location navigate to /')
          navigate('/')
        }
      }, 1000)

      return () => clearTimeout(timeout)
    }
  }, [poolLytOverview, navigate])

  if (!poolLytOverview) {
    return <PoolDetailsSkeleton />
  }

  return (
    <Box px={3} py={2} sx={{ flexGrow: 1 }}>
      <Grid alignItems={'stretch'} container spacing={8} sx={{ flexGrow: 1 }}>
        <Grid xs={12} key={'Summary'}>
          <SectionHeader>Summary</SectionHeader>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={{
              ...PoolLytOverviewCTOSchema.describe().fields.totalOutstandingLoanPrincipal,
              label: 'Total Value of SIERRA Outstanding',
            }}
            liquidityTokenSymbol={poolLytOverview.liquidityTokenSymbol}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.totalShares}
            liquidityTokenSymbol={poolLytOverview.liquidityTokenSymbol}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.totalValueOfReservesInSierra}
            liquidityTokenSymbol={poolLytOverview.liquidityTokenSymbol}
          ></ViewField>
        </Grid>

        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.interestRate}
          ></ViewField>
        </Grid>

        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.benchmarkRate}
          ></ViewField>
        </Grid>

        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            poolTokenSymbol={poolLytOverview.symbol}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.exchangeRate}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            poolTokenSymbol={poolLytOverview.symbol}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.exchangeRateType}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.indicativeInterestRate}
          ></ViewField>
        </Grid>

        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.collateralRate}
          ></ViewField>
        </Grid>
        <Grid xs={12} key={'VaultConfig'}>
          <SectionHeader>Vault Configuration</SectionHeader>
        </Grid>

        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.closeOfDepositTime}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.closeOfWithdrawTime}
          ></ViewField>
        </Grid>

        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.transferInDays}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.transferOutDays}
          ></ViewField>
        </Grid>

        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.advisoryFeeRate}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.platformFeeRate}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.liquidityFeeRate}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.contractualCurrency}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.minimumTransferAmount}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.maximumTransferAmount}
          ></ViewField>
        </Grid>

        <Grid xs={12} key={'HistPerformance'}>
          <SectionHeader>Historical Performance</SectionHeader>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.totalAssetsDeposited}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.totalAssetsWithdrawn}
            liquidityTokenSymbol={poolLytOverview.liquidityTokenSymbol}
          ></ViewField>
        </Grid>
        <Grid xs={12} sm={6}>
          <ViewField
            dataObject={poolLytOverview}
            fieldSchema={PoolLytOverviewCTOSchema.describe().fields.totalInterestAccrued}
            liquidityTokenSymbol={poolLytOverview.liquidityTokenSymbol}
          ></ViewField>
        </Grid>
      </Grid>
    </Box>
  )
}
